import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './dialog';
import { Button } from './button';
import { Textarea } from './textarea';
import { ScrollArea } from './scroll-area';
import { Info, AlertCircle, FileSpreadsheet, Loader2, CheckCircle2, FileText, Upload } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import api from '../../api';

// Helper to find header values case-insensitively
const getRowValue = (row, columnName) => {
    const cleanColName = columnName.trim().toLowerCase();
    for (const key of Object.keys(row)) {
        if (key.trim().toLowerCase() === cleanColName) {
            return row[key];
        }
    }
    return undefined;
};

// Robust line parsing respecting double quotes and escapes
function parseCSVLine(line, delimiter = ',') {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
            // Check for escaped double quotes
            if (inQuotes && line[i + 1] === '"') {
                current += '"';
                i++; // Skip the next quote
            } else {
                inQuotes = !inQuotes;
            }
        } else if (char === delimiter && !inQuotes) {
            result.push(current.trim());
            current = '';
        } else {
            current += char;
        }
    }
    result.push(current.trim());
    return result;
}

// Robust, flexible date parsing supporting DD/MM/YYYY, MM/DD/YYYY, and YYYY-MM-DD formats
const parseFlexibleDate = (dStr) => {
    if (!dStr) return null;
    const clean = dStr.trim();
    if (!clean) return null;

    // Try ISO format: YYYY-MM-DD or YYYY/MM/DD
    const isoMatch = clean.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
    if (isoMatch) {
        const y = isoMatch[1];
        const m = isoMatch[2].padStart(2, '0');
        const d = isoMatch[3].padStart(2, '0');
        return `${y}-${m}-${d}`;
    }

    // Try standard formats: D/M/YYYY or M/D/YYYY
    const partsMatch = clean.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
    if (partsMatch) {
        const p1 = parseInt(partsMatch[1], 10);
        const p2 = parseInt(partsMatch[2], 10);
        const y = partsMatch[3];
        
        let m, d;
        if (p1 > 12) {
            // Must be DD/MM/YYYY
            d = String(p1).padStart(2, '0');
            m = String(p2).padStart(2, '0');
        } else if (p2 > 12) {
            // Must be MM/DD/YYYY
            m = String(p1).padStart(2, '0');
            d = String(p2).padStart(2, '0');
        } else {
            // Default to DD/MM/YYYY (Day-Month-Year)
            d = String(p1).padStart(2, '0');
            m = String(p2).padStart(2, '0');
        }
        return `${y}-${m}-${d}`;
    }

    // Native Date fallback (for full ISO strings or browser date formats)
    const timestamp = Date.parse(clean);
    if (!isNaN(timestamp)) {
        const dateObj = new Date(timestamp);
        const y = dateObj.getFullYear();
        const m = String(dateObj.getMonth() + 1).padStart(2, '0');
        const d = String(dateObj.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }

    return null;
};

const TEMPLATES = {
    students: {
        columns: ['Name', 'Email', 'Enrollment Number', 'Course Name', 'Branch Name', 'Batch Name', 'Subject Names'],
        sampleData: [
            ['Aarav Sharma', 'aarav.sharma@example.com', 'EN2026001', 'CA Foundation', 'Charni Road', 'CA Foundation Sep 26 Charni Road', 'Accounts, Economics']
        ],
        resolver: async (team_id, rows) => {
            const [courses, branches, batches, subjects, existing] = await Promise.all([
                supabase.from('courses').select('id, name').eq('team_id', team_id),
                supabase.from('branches').select('id, name').eq('team_id', team_id),
                supabase.from('batches').select('id, name, course_id').eq('team_id', team_id),
                supabase.from('subjects').select('id, name, course_id').eq('team_id', team_id),
                supabase.from('students').select('email, enrollment_number').eq('team_id', team_id)
            ]);

            const courseMap = {}; courses.data?.forEach(c => courseMap[c.name.trim().toLowerCase()] = c.id);
            const branchMap = {}; branches.data?.forEach(c => branchMap[c.name.trim().toLowerCase()] = c.id);
            const batchMap = {}; batches.data?.forEach(c => batchMap[c.name.trim().toLowerCase()] = { id: c.id, course_id: c.course_id });
            // Subject map keyed by "course_id::name" for disambiguation, and also by name alone
            const subjectMapByCourseAndName = {};
            const subjectMapByName = {};
            subjects.data?.forEach(s => {
                const key = `${s.course_id}::${s.name.trim().toLowerCase()}`;
                subjectMapByCourseAndName[key] = s.id;
                // Name-only map: last write wins, fine for non-ambiguous names
                subjectMapByName[s.name.trim().toLowerCase()] = s.id;
            });

            const existingEmails = new Set(existing.data?.map(e => e.email?.trim().toLowerCase()).filter(Boolean));
            const existingEnrollments = new Set(existing.data?.map(e => e.enrollment_number?.trim().toLowerCase()).filter(Boolean));

            let seenEmails = new Set();
            let seenEnr = new Set();

            return rows.map((r, i) => {
                const name = (getRowValue(r, 'Name') || '').trim();
                const email = (getRowValue(r, 'Email') || '').trim();
                const enr = (getRowValue(r, 'Enrollment Number') || '').trim();
                const courseName = (getRowValue(r, 'Course Name') || '').trim();
                const branchName = (getRowValue(r, 'Branch Name') || '').trim();
                const batchName = (getRowValue(r, 'Batch Name') || '').trim();
                const subjectNamesRaw = (getRowValue(r, 'Subject Names') || '').trim();

                const errors = [];
                let isDuplicate = false;

                if (!name) errors.push('Name is required');

                if (email) {
                    if (existingEmails.has(email.toLowerCase()) || seenEmails.has(email.toLowerCase())) {
                        isDuplicate = true;
                        errors.push('Duplicate email');
                    }
                    seenEmails.add(email.toLowerCase());
                }

                if (enr) {
                    if (existingEnrollments.has(enr.toLowerCase()) || seenEnr.has(enr.toLowerCase())) {
                        isDuplicate = true;
                        errors.push('Duplicate enrollment number');
                    }
                    seenEnr.add(enr.toLowerCase());
                }

                let course_id = courseName ? courseMap[courseName.toLowerCase()] : null;
                let branch_id = branchName ? branchMap[branchName.toLowerCase()] : null;
                let batch_id = null;

                if (courseName && !course_id) errors.push(`Course not found: ${courseName}`);
                if (branchName && !branch_id) errors.push(`Branch not found: ${branchName}`);
                
                if (batchName) {
                    const mappedBatch = batchMap[batchName.toLowerCase()];
                    if (!mappedBatch) {
                        errors.push(`Batch not found: ${batchName}`);
                    } else {
                        batch_id = mappedBatch.id;
                        if (!course_id) course_id = mappedBatch.course_id;
                    }
                }

                // Resolve subject_ids — NULL means "All Subjects" (default)
                let subject_ids = null;
                if (subjectNamesRaw) {
                    const names = subjectNamesRaw.split(',').map(s => s.trim()).filter(Boolean);
                    const resolvedIds = [];
                    for (const sName of names) {
                        const sLower = sName.toLowerCase();
                        // Prefer course-scoped lookup if course_id is known
                        const scopedId = course_id ? subjectMapByCourseAndName[`${course_id}::${sLower}`] : undefined;
                        const id = scopedId || subjectMapByName[sLower];
                        if (id) {
                            resolvedIds.push(id);
                        } else {
                            errors.push(`Subject not found: "${sName}"`);
                        }
                    }
                    if (resolvedIds.length > 0 && resolvedIds.length === names.length) {
                        subject_ids = resolvedIds;
                    }
                }

                return {
                    id: i,
                    original: r,
                    parsed: { name, email: email || null, enrollment_number: enr || null, course_id, branch_id, batch_id, subject_ids, team_id, attendance_pct: 100.0 },
                    errors,
                    isDuplicate
                };
            });
        }
    },
    faculty: {
        columns: ['name', 'email', 'phone', 'max_hours_per_day', 'status'],
        sampleData: [
            ['Jay Sir', 'jayparekh@gmail.com', '', '12', 'active']
        ],
        resolver: async (team_id, rows) => {
            const { data: existing } = await supabase.from('faculty').select('email').eq('team_id', team_id);
            const existingEmails = new Set(existing?.map(e => e.email?.trim().toLowerCase()).filter(Boolean));
            let seenEmails = new Set();

            return rows.map((r, i) => {
                const name = (getRowValue(r, 'name') || '').trim();
                const email = (getRowValue(r, 'email') || '').trim();
                const phone = (getRowValue(r, 'phone') || '').trim();
                const hrsVal = getRowValue(r, 'max_hours_per_day');
                const max_hours_per_day = hrsVal ? parseInt(hrsVal) || 6 : 6;
                const status = (getRowValue(r, 'status') || 'active').trim().toLowerCase();

                const errors = [];
                let isDuplicate = false;

                if (!name) errors.push('Name is required');
                if (!email) errors.push('Email is required');
                else {
                    if (existingEmails.has(email.toLowerCase()) || seenEmails.has(email.toLowerCase())) {
                        isDuplicate = true;
                        errors.push('Duplicate email');
                    }
                    seenEmails.add(email.toLowerCase());
                }

                return {
                    id: i,
                    original: r,
                    parsed: { name, email, phone, max_hours_per_day, status, team_id },
                    errors,
                    isDuplicate
                };
            });
        }
    },
    batches: {
        columns: ['name', 'code', 'course code', 'Branch acronym', 'strength', 'importance', 'max_events_per_day', 'course_duration_weeks', 'course_start_date', 'course_end_date'],
        sampleData: [
            ['CA Foundation Sep 26 Charni Road', 'CAFCCRS26', 'CAFC', 'CR', '80', '3', '2', '16', '09/04/2026', '09/08/2026']
        ],
        resolver: async (team_id, rows) => {
            const [courses, branches, existing] = await Promise.all([
                supabase.from('courses').select('id, code').eq('team_id', team_id),
                supabase.from('branches').select('id, code').eq('team_id', team_id),
                supabase.from('batches').select('code').eq('team_id', team_id)
            ]);
            
            const courseMap = {}; courses.data?.forEach(c => courseMap[c.code.trim().toLowerCase()] = c.id);
            const branchMap = {}; branches.data?.forEach(c => branchMap[c.code.trim().toLowerCase()] = c.id);

            const existingCodes = new Set(existing.data?.map(e => e.code?.trim().toLowerCase()).filter(Boolean));
            let seenCodes = new Set();

            return rows.map((r, i) => {
                const name = (getRowValue(r, 'name') || '').trim();
                const code = (getRowValue(r, 'code') || '').trim();
                const courseCode = (getRowValue(r, 'course code') || '').trim();
                const branchAcronym = (getRowValue(r, 'Branch acronym') || '').trim();
                const strength = parseInt(getRowValue(r, 'strength') || '0') || 0;
                const importance = Math.max(1, Math.min(5, parseInt(getRowValue(r, 'importance') || '3') || 3));
                const maxEvents = Math.max(1, Math.min(6, parseInt(getRowValue(r, 'max_events_per_day') || '2') || 2));
                const durationVal = getRowValue(r, 'course_duration_weeks');
                const course_duration_weeks = durationVal ? parseInt(durationVal) || 16 : 16;

                const errors = [];
                let isDuplicate = false;

                if (!name) errors.push('Name is required');
                if (!code) errors.push('Code is required');
                else {
                    if (existingCodes.has(code.toLowerCase()) || seenCodes.has(code.toLowerCase())) {
                        isDuplicate = true;
                        errors.push('Duplicate batch code');
                    }
                    seenCodes.add(code.toLowerCase());
                }

                const course_id = courseCode ? courseMap[courseCode.toLowerCase()] : null;
                const branch_id = branchAcronym ? branchMap[branchAcronym.toLowerCase()] : null;

                if (courseCode && !course_id) errors.push(`Course not found for code: ${courseCode}`);
                if (branchAcronym && !branch_id) errors.push(`Branch not found for code/acronym: ${branchAcronym}`);

                const course_start_date = parseFlexibleDate(getRowValue(r, 'course_start_date'));
                const course_end_date = parseFlexibleDate(getRowValue(r, 'course_end_date'));

                return {
                    id: i,
                    original: r,
                    parsed: { name, code, course_id, branch_id, strength, importance, max_events_per_day: maxEvents, course_duration_weeks, course_start_date, course_end_date, team_id },
                    errors,
                    isDuplicate
                };
            });
        }
    },
    courses: {
        columns: ['name', 'code', 'priority'],
        sampleData: [
            ['CFA', 'CFA', '3']
        ],
        resolver: async (team_id, rows) => {
            const { data: existing } = await supabase.from('courses').select('code').eq('team_id', team_id);
            const existingCodes = new Set(existing?.map(e => e.code?.trim().toLowerCase()).filter(Boolean));
            let seenCodes = new Set();

            return rows.map((r, i) => {
                const name = (getRowValue(r, 'name') || '').trim();
                const code = (getRowValue(r, 'code') || '').trim();
                const priority = Math.max(1, Math.min(5, parseInt(getRowValue(r, 'priority') || '3') || 3));

                const errors = [];
                let isDuplicate = false;

                if (!name) errors.push('Name is required');
                if (!code) errors.push('Code is required');
                else {
                    if (existingCodes.has(code.toLowerCase()) || seenCodes.has(code.toLowerCase())) {
                        isDuplicate = true;
                        errors.push('Duplicate course code');
                    }
                    seenCodes.add(code.toLowerCase());
                }

                return {
                    id: i,
                    original: r,
                    parsed: { name, code, priority, team_id },
                    errors,
                    isDuplicate
                };
            });
        }
    },
    subjects: {
        columns: ['Name', 'Course Name', 'Credit Hours', 'Completed Hours'],
        sampleData: [
            ['Accounts', 'CFA', '60', '0']
        ],
        resolver: async (team_id, rows) => {
            const [courses, existing] = await Promise.all([
                supabase.from('courses').select('id, name').eq('team_id', team_id),
                supabase.from('subjects').select('name, course_id').eq('team_id', team_id)
            ]);
            
            const courseMap = {}; courses.data?.forEach(c => courseMap[c.name.trim().toLowerCase()] = c.id);
            const existingKeys = new Set(existing.data?.map(s => `${s.course_id}_${s.name.trim().toLowerCase()}`).filter(Boolean));
            let seenKeys = new Set();

            return rows.map((r, i) => {
                const name = (getRowValue(r, 'Name') || '').trim();
                const courseName = (getRowValue(r, 'Course Name') || '').trim();
                const creditHours = parseInt(getRowValue(r, 'Credit Hours') || '0') || 0;
                const completedHours = parseInt(getRowValue(r, 'Completed Hours') || '0') || 0;

                const errors = [];
                let isDuplicate = false;

                if (!name) errors.push('Name is required');
                if (!courseName) errors.push('Course Name is required');

                let course_id = null;
                if (courseName) {
                    course_id = courseMap[courseName.toLowerCase()];
                    if (!course_id) {
                        errors.push('Unidentified Course');
                    }
                }

                if (name && course_id) {
                    const key = `${course_id}_${name.toLowerCase()}`;
                    if (existingKeys.has(key) || seenKeys.has(key)) {
                        isDuplicate = true;
                        errors.push('Duplicate subject for this course');
                    }
                    seenKeys.add(key);
                }

                return {
                    id: i,
                    original: r,
                    parsed: { name, course_id, credit_hours: creditHours, completed_hours: completedHours, team_id },
                    errors,
                    isDuplicate
                };
            });
        }
    },
    branches: {
        columns: ['name', 'code', 'rooms'],
        sampleData: [
            ['Ghatkopar', 'GK', '["Room 1"]']
        ],
        resolver: async (team_id, rows) => {
            const { data: existing } = await supabase.from('branches').select('code').eq('team_id', team_id);
            const existingCodes = new Set(existing?.map(e => e.code?.trim().toLowerCase()).filter(Boolean));
            let seenCodes = new Set();

            return rows.map((r, i) => {
                const name = (getRowValue(r, 'name') || '').trim();
                const code = (getRowValue(r, 'code') || '').trim();
                const roomsVal = getRowValue(r, 'rooms') || '';

                const errors = [];
                let isDuplicate = false;

                if (!name) errors.push('Name is required');
                if (!code) errors.push('Code is required');
                else {
                    if (existingCodes.has(code.toLowerCase()) || seenCodes.has(code.toLowerCase())) {
                        isDuplicate = true;
                        errors.push('Duplicate branch code');
                    }
                    seenCodes.add(code.toLowerCase());
                }

                let rooms = [];
                if (roomsVal) {
                    try {
                        const parsed = JSON.parse(roomsVal);
                        if (Array.isArray(parsed)) {
                            rooms = parsed.map(String).map(s => s.trim()).filter(Boolean);
                        } else {
                            rooms = [String(parsed).trim()];
                        }
                    } catch (e) {
                        rooms = roomsVal.split(',').map(s => s.trim()).filter(Boolean);
                    }
                }

                return {
                    id: i,
                    original: r,
                    parsed: { name, code, rooms, color: null, team_id },
                    errors,
                    isDuplicate
                };
            });
        }
    },
    travel_times: {
        columns: ['From Branch Code', 'To Branch Code', 'Travel Duration'],
        sampleData: [
            ['CR', 'GK', '60']
        ],
        resolver: async (team_id, rows) => {
            const { data: branches } = await supabase.from('branches').select('id, code').eq('team_id', team_id);
            const branchMap = {};
            branches?.forEach(b => {
                branchMap[b.code.trim().toLowerCase()] = b.id;
            });

            return rows.map((r, i) => {
                const fromCode = (getRowValue(r, 'From Branch Code') || '').trim();
                const toCode = (getRowValue(r, 'To Branch Code') || '').trim();
                const durationVal = getRowValue(r, 'Travel Duration') || '60';

                const errors = [];

                if (!fromCode) errors.push('From Branch Code is required');
                if (!toCode) errors.push('To Branch Code is required');

                const branch_from_id = branchMap[fromCode.toLowerCase()];
                const branch_to_id = branchMap[toCode.toLowerCase()];

                if (fromCode && !branch_from_id) errors.push(`Branch not found for code: ${fromCode}`);
                if (toCode && !branch_to_id) errors.push(`Branch not found for code: ${toCode}`);

                let minutes = 60;
                if (durationVal) {
                    const match = durationVal.match(/\d+/);
                    if (match) {
                        minutes = parseInt(match[0]) || 60;
                    }
                }

                return {
                    id: i,
                    original: r,
                    parsed: { branch_from_id, branch_to_id, minutes, team_id },
                    errors,
                    isDuplicate: false
                };
            });
        }
    },
    faculty_assignments: {
        columns: ['Faculty Name', 'Subject Code', 'Batch Code', 'Hours of lecture per week', 'Min Slot Hours', 'Max Slot Hours', 'Color Preference', 'Group ID'],
        sampleData: [
            ['Shridhar Sir', 'Accounts', 'CAFCCRS26', '12', '2', '2', 'orange', '']
        ],
        resolver: async (team_id, rows) => {
            const [faculties, batches, subjects] = await Promise.all([
                supabase.from('faculty').select('id, name').eq('team_id', team_id),
                supabase.from('batches').select('id, code, course_id').eq('team_id', team_id),
                supabase.from('subjects').select('id, name, course_id').eq('team_id', team_id)
            ]);

            const facultyMap = {}; faculties.data?.forEach(f => facultyMap[f.name.trim().toLowerCase()] = f.id);
            const batchMap = {}; batches.data?.forEach(b => batchMap[b.code.trim().toLowerCase()] = { id: b.id, course_id: b.course_id });
            
            const subjectMap = {}; subjects.data?.forEach(s => {
                subjectMap[`${s.course_id}_${s.name.trim().toLowerCase()}`] = s.id;
            });

            const validColors = ['blue', 'violet', 'rose', 'emerald', 'orange', 'amber', 'indigo', 'teal', 'pink', 'slate', 'cyan', 'lime', 'yellow', 'red', 'purple', 'fuchsia', 'green'];

            return rows.map((r, i) => {
                const facName = (getRowValue(r, 'Faculty Name') || '').trim();
                const subjCode = (getRowValue(r, 'Subject Code') || '').trim();
                const batchCode = (getRowValue(r, 'Batch Code') || '').trim();
                const hoursVal = getRowValue(r, 'Hours of lecture per week') || '3';
                const minSlotVal = getRowValue(r, 'Min Slot Hours') || '1';
                const maxSlotVal = getRowValue(r, 'Max Slot Hours') || '3';
                
                const rawColorVal = (getRowValue(r, 'Color Preference') || '').trim();
                const colorVal = rawColorVal || 'blue';

                const groupId = (getRowValue(r, 'Group ID') || '').trim() || null;

                const errors = [];

                if (!facName) errors.push('Faculty Name is required');
                if (!subjCode) errors.push('Subject Code/Name is required');
                if (!batchCode) errors.push('Batch Code is required');

                if (rawColorVal && !validColors.includes(rawColorVal)) {
                    errors.push(`Color Preference "${rawColorVal}" is invalid. Must be exactly one of: ${validColors.join(', ')}`);
                }

                const faculty_id = facultyMap[facName.toLowerCase()];
                const batchInfo = batchMap[batchCode.toLowerCase()];
                const batch_id = batchInfo?.id;
                
                let subject_id = null;
                if (batchInfo && subjCode) {
                    subject_id = subjectMap[`${batchInfo.course_id}_${subjCode.toLowerCase()}`];
                }

                if (facName && !faculty_id) errors.push(`Faculty not found: ${facName}`);
                if (batchCode && !batch_id) errors.push(`Batch not found: ${batchCode}`);
                if (subjCode && batchInfo && !subject_id) errors.push(`Subject not found: "${subjCode}" for course of batch ${batchCode}`);

                const hours_per_week = parseInt(hoursVal) || 3;
                const min_slot_hours = parseFloat(minSlotVal) || 1.0;
                const max_slot_hours = parseFloat(maxSlotVal) || 3.0;

                return {
                    id: i,
                    original: r,
                    parsed: { faculty_id, subject_id, batch_id, hours_per_week, min_slot_hours, max_slot_hours, color: colorVal, group_id: groupId, team_id },
                    errors,
                    isDuplicate: false
                };
            });
        }
    },
    benchrex_users: {
        columns: ['Name', 'Email', 'Role', 'Credits', 'Grade', 'Course', 'Batch'],
        sampleData: [
            ['Aarav Sharma', 'aarav.sharma@example.com', 'student', '100', 'A', 'CA Foundation', 'CA Foundation Sep 26 Charni Road']
        ],
        resolver: async (team_id, rows) => {
            const { data: existing } = await supabase.from('benchrex_users').select('email').eq('team_id', team_id);
            const existingEmails = new Set(existing?.map(e => e.email?.trim().toLowerCase()).filter(Boolean));
            let seenEmails = new Set();

            return rows.map((r, i) => {
                const name = (getRowValue(r, 'Name') || '').trim();
                const email = (getRowValue(r, 'Email') || '').trim();
                const role = (getRowValue(r, 'Role') || 'student').toLowerCase().trim();
                const credits = parseInt(getRowValue(r, 'Credits') || '100') || 100;
                const grade = (getRowValue(r, 'Grade') || '').trim();
                const course = (getRowValue(r, 'Course') || '').trim();
                const batch = (getRowValue(r, 'Batch') || '').trim();

                const errors = [];
                let isDuplicate = false;

                if (!name) errors.push('Name is required');
                if (!email) errors.push('Email is required');
                else {
                    if (existingEmails.has(email.toLowerCase()) || seenEmails.has(email.toLowerCase())) {
                        isDuplicate = true;
                        errors.push('Duplicate email');
                    }
                    seenEmails.add(email.toLowerCase());
                }

                return {
                    id: i,
                    original: r,
                    parsed: { name, email, role, credits, grade, course, batch, team_id },
                    errors,
                    isDuplicate
                };
            });
        }
    },
    inquiries: {
        columns: ['Name', 'Inquiry Date', 'Course Name', 'Contact', 'Phone Numbers', 'Description', 'Tag'],
        sampleData: [
            ['Aarav Sharma', '2026-06-03', 'B.Tech CSE', 'aarav@example.com', '+919999999999|+918888888888', 'Interested in the program', 'positive']
        ],
        resolver: async (team_id, rows) => {
            return rows.map((r, i) => {
                const name = (getRowValue(r, 'Name') || '').trim();
                const contact = (getRowValue(r, 'Contact') || '').trim();
                const course_name = (getRowValue(r, 'Course Name') || '').trim();
                const description = (getRowValue(r, 'Description') || '').trim();
                const tag = (getRowValue(r, 'Tag') || 'positive').toLowerCase().trim();
                const inquiry_date = parseFlexibleDate(getRowValue(r, 'Inquiry Date'));

                // Phone Numbers: pipe-separated list e.g. "+91999|+91888"
                const phoneRaw = (getRowValue(r, 'Phone Numbers') || '').trim();
                const contact_numbers = phoneRaw
                    ? phoneRaw.split('|').map(n => n.trim()).filter(Boolean)
                    : [];

                const errors = [];
                if (!name) errors.push('Name is required');

                return {
                    id: i,
                    original: r,
                    parsed: { name, contact, contact_numbers, course_name, description, tag, inquiry_date, team_id },
                    errors,
                    isDuplicate: false
                };
            });
        }
    }
};

export function BulkImportModal({ open, onOpenChange, entityName, onImportComplete }) {
    const [rawText, setRawText] = useState('');
    const [analyzedData, setAnalyzedData] = useState(null);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [importSuccess, setImportSuccess] = useState(null);

    // Reset when modal closes
    useEffect(() => {
        if (!open) {
            setTimeout(() => {
                setRawText('');
                setAnalyzedData(null);
                setImportSuccess(null);
            }, 300);
        }
    }, [open]);

    const handleDownloadSample = () => {
        const temp = TEMPLATES[entityName];
        if (!temp) return;

        // Construct CSV
        const headerRow = temp.columns.map(c => `"${c.replace(/"/g, '""')}"`).join(',');
        const sampleRows = temp.sampleData.map(row => 
            row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(',')
        ).join('\n');
        const csvContent = `${headerRow}\n${sampleRows}`;

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `${entityName}_sample.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("Sample CSV template downloaded!");
    };

    const handleAnalyze = async () => {
        if (!rawText.trim()) {
            toast.error("Please paste or upload data first");
            return;
        }

        setIsAnalyzing(true);
        try {
            const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
            const team_id = profile.team_id;

            const lines = rawText.split(/\r?\n/).filter(l => l.trim());
            if (lines.length < 1) throw new Error("File appears to be empty.");

            const template = TEMPLATES[entityName];
            if (!template) throw new Error("Unknown entity template.");

            // Smart delimiter detection
            const firstLine = lines[0];
            const tabCount = (firstLine.match(/\t/g) || []).length;
            const commaCount = (firstLine.match(/,/g) || []).length;
            const delimiter = tabCount > commaCount ? '\t' : ',';

            const firstLineValues = parseCSVLine(firstLine, delimiter).map(h => h.trim());
            
            // Detect if the first line is a header row
            // It is a header row if any of its values match a column in the template (case-insensitive)
            const hasHeaderRow = firstLineValues.some(val => 
                template.columns.some(col => col.toLowerCase() === val.toLowerCase())
            );

            let headers;
            let dataLines;

            if (hasHeaderRow) {
                headers = firstLineValues;
                dataLines = lines.slice(1);
            } else {
                headers = template.columns;
                dataLines = lines;
            }
            
            // Raw parsed objects
            const rawRows = dataLines.map(line => {
                const values = parseCSVLine(line, delimiter);
                const rowObj = {};
                headers.forEach((h, idx) => { rowObj[h] = values[idx] || ''; });
                return rowObj;
            });

            // Filter out comment lines, instructions, or duplicate headers
            const filteredRows = rawRows.filter(r => {
                // If it's a duplicate header row, skip it (only if we parsed a header row)
                if (hasHeaderRow) {
                    const firstVal = (getRowValue(r, template.columns[0]) || '').trim();
                    if (firstVal.toLowerCase() === template.columns[0].toLowerCase()) return false;
                }
                
                // If it looks like a comment/instruction row (contains keywords like "by default" or "short code")
                let hasCommentKeywords = false;
                for (const val of Object.values(r)) {
                    const cleanVal = String(val).toLowerCase();
                    if (
                        cleanVal.includes('by default') || 
                        cleanVal.includes('short code') || 
                        cleanVal.includes('required') || 
                        cleanVal.includes('faculty name as') || 
                        cleanVal.includes('email id (for') ||
                        cleanVal.includes('total students') ||
                        cleanVal.includes('lecture generally')
                    ) {
                        hasCommentKeywords = true;
                        break;
                    }
                }
                if (hasCommentKeywords) return false;

                // Also skip empty rows
                const allEmpty = Object.values(r).every(v => !String(v).trim());
                if (allEmpty) return false;

                return true;
            });

            if (filteredRows.length === 0) {
                throw new Error("No valid data rows found after filtering out headers and instruction rows.");
            }

            if (hasHeaderRow) {
                const missingHeaders = template.columns.filter(c => !headers.some(h => h.toLowerCase() === c.toLowerCase()));
                if (missingHeaders.length > 0) {
                    toast.warning(`Missing columns: ${missingHeaders.join(', ')}.\nThey will be considered empty for all rows.`);
                }
            }

            const analyzed = await template.resolver(team_id, filteredRows);
            setAnalyzedData(analyzed);
        } catch (err) {
            console.error(err);
            toast.error(err.message || 'Failed to analyze data. Please make sure columns are separated by tabs or commas.');
        } finally {
            setIsAnalyzing(false);
        }
    };

    const handleImport = async () => {
        if (!analyzedData) return;
        
        const validRows = analyzedData.filter(r => r.errors.length === 0).map(r => r.parsed);
        if (validRows.length === 0) {
            toast.error('No valid rows found to import.');
            return;
        }

        setIsImporting(true);
        try {
            // For travel_times, expand both directions before sending to backend
            let importRows = validRows;
            if (entityName === 'travel_times') {
                const expanded = [];
                validRows.forEach(item => {
                    expanded.push(item);
                    expanded.push({
                        branch_from_id: item.branch_to_id,
                        branch_to_id: item.branch_from_id,
                        minutes: item.minutes,
                        team_id: item.team_id
                    });
                });
                importRows = expanded;
            }

            // Chunked insert
            for (let i = 0; i < importRows.length; i += 500) {
                const chunk = importRows.slice(i, i + 500);
                
                if (entityName === 'benchrex_users') {
                    const res = await api.post('/api/benchrex/bulk-register-users', chunk);
                    if (res.data.failed > 0) {
                        toast.warning(`${res.data.failed} users failed to sync with Auth.`);
                    }
                } else {
                    await api.post(`/api/import/${entityName}`, { rows: chunk });
                }
            }

            toast.success(`Successfully imported ${validRows.length} ${entityName}!`);
            setImportSuccess({
                count: validRows.length,
                entity: entityName,
                rows: validRows
            });
            onImportComplete?.();
        } catch (err) {
            console.error(err);
            toast.error(err.message || `Failed to import ${entityName}`);
        } finally {
            setIsImporting(false);
        }
    };

    const template = TEMPLATES[entityName];
    if (!template) return null;

    const validCount = analyzedData ? analyzedData.filter(r => r.errors.length === 0).length : 0;
    const invalidCount = analyzedData ? analyzedData.length - validCount : 0;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-[95vw] md:max-w-4xl lg:max-w-5xl w-full" aria-describedby="bulk-import-description">
                <DialogHeader>
                    <DialogTitle className="capitalize">Bulk Import {entityName.replace('_', ' ')}</DialogTitle>
                    <DialogDescription id="bulk-import-description">
                        Paste data directly from Excel or Google Sheets, or upload a CSV file.
                    </DialogDescription>
                </DialogHeader>

                {importSuccess ? (
                    <div className="py-12 flex flex-col items-center justify-center text-center space-y-4">
                        <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 mb-2">
                            <CheckCircle2 className="w-10 h-10" />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-zinc-900 dark:text-zinc-100 italic">Import Successful!</h3>
                            <p className="text-sm text-zinc-500 max-w-xs mx-auto mt-1">
                                {importSuccess.count} {importSuccess.entity.replace('_', ' ')} records have been added to your database.
                            </p>
                        </div>
                        <div className="flex flex-col gap-2 w-full max-w-xs pt-4">
                            <Button 
                                onClick={() => onOpenChange(false)}
                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold h-12 rounded-xl"
                            >
                                Done
                            </Button>
                        </div>
                        <p className="text-[10px] text-zinc-400 max-w-xs mx-auto mt-4 px-4 bg-zinc-50 dark:bg-zinc-950/30 py-2 rounded-lg border border-zinc-100 dark:border-zinc-800">
                             <Info className="w-3 h-3 inline mr-1 mb-0.5" /> 
                             Note: This data is stored securely and used only for internal calendar management.
                        </p>
                    </div>
                ) : !analyzedData ? (
                    <div className="space-y-4 w-full">
                        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-200 dark:border-blue-800/30 text-sm">
                            <div className="flex gap-2 text-blue-700 dark:text-blue-300 font-semibold mb-1">
                                <Info className="w-4 h-4" /> Required Columns (exact match)
                            </div>
                            <p className="text-blue-600 dark:text-blue-400 pl-6 break-words">
                                {template.columns.join('  •  ')}
                            </p>
                        </div>

                        <div className="flex flex-wrap items-center justify-between gap-3 bg-zinc-50 dark:bg-zinc-900/50 p-3 rounded-xl border border-zinc-150 dark:border-zinc-800">
                            <div className="flex items-center gap-2">
                                <input
                                    type="file"
                                    accept=".csv,.txt"
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                            const reader = new FileReader();
                                            reader.onload = (evt) => {
                                                setRawText(evt.target.result);
                                            };
                                            reader.readAsText(file);
                                        }
                                    }}
                                    className="hidden"
                                    id="csv-file-upload"
                                />
                                <label htmlFor="csv-file-upload" className="cursor-pointer">
                                    <span className="inline-flex items-center justify-center rounded-xl text-xs font-bold transition-all border border-zinc-200 dark:border-zinc-800 bg-white hover:bg-zinc-100 hover:text-accent-foreground h-9 px-4 py-2 gap-2 shadow-sm text-zinc-700 dark:text-zinc-300">
                                        <Upload className="w-4 h-4 text-blue-500" />
                                        Upload CSV File
                                    </span>
                                </label>
                                <span className="text-[10px] text-zinc-400 uppercase font-semibold">or paste content below</span>
                            </div>
                            
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={handleDownloadSample}
                                className="h-9 rounded-xl text-xs font-bold gap-2 border-zinc-200 text-zinc-700 dark:text-zinc-300 bg-white hover:bg-zinc-100"
                            >
                                <FileSpreadsheet className="w-4 h-4 text-emerald-500" />
                                Download Sample
                            </Button>
                        </div>

                        <Textarea 
                            placeholder="Paste your spreadsheet data here... (tab-separated or comma-separated)" 
                            className="min-h-[250px] font-mono text-xs whitespace-pre w-full"
                            value={rawText}
                            onChange={(e) => setRawText(e.target.value)}
                        />

                        <div className="flex items-center gap-2 p-2 bg-zinc-50 dark:bg-zinc-900/50 rounded-lg border border-zinc-100 dark:border-zinc-800">
                            <div className="p-1.5 rounded-md bg-zinc-200 dark:bg-zinc-800 text-zinc-500">
                                <AlertCircle className="w-3 h-3" />
                            </div>
                            <p className="text-[10px] text-zinc-500 leading-tight">
                                <span className="font-bold text-zinc-700 dark:text-zinc-300">Privacy Notice:</span> All entity data is stored securely and used exclusively for calendar synchronization. No advertising or third-party sharing.
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4 w-full min-w-0 overflow-hidden">
                        <div className="flex items-center justify-between py-2 border-b border-zinc-100 dark:border-zinc-800">
                            <div className="flex items-center gap-4">
                                <span className="flex items-center gap-1.5 text-sm font-medium text-emerald-600">
                                    <CheckCircle2 className="w-4 h-4" />
                                    {validCount} Ready
                                </span>
                                <span className="flex items-center gap-1.5 text-sm font-medium text-red-600">
                                    <AlertCircle className="w-4 h-4" />
                                    {invalidCount} Invalid (Skipped)
                                </span>
                            </div>
                            <span className="text-[10px] text-zinc-400 font-mono">
                                Ingestion Status: COMPLIANT
                            </span>
                        </div>

                        <div className="h-[400px] w-full border border-zinc-200 dark:border-zinc-700 rounded-lg bg-zinc-50 dark:bg-zinc-900 overflow-auto relative custom-scrollbar">
                            <div className="min-w-fit w-full">
                                <table className="w-full text-left text-xs">
                                    <thead className="bg-white dark:bg-zinc-800 sticky top-0 z-10 shadow-sm">
                                        <tr>
                                            <th className="px-3 py-2 border-b font-semibold w-10">#</th>
                                            <th className="px-3 py-2 border-b font-semibold">Status</th>
                                            {template.columns.map(c => <th key={c} className="px-3 py-2 border-b font-semibold whitespace-nowrap">{c}</th>)}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {analyzedData.map(row => {
                                            const isValid = row.errors.length === 0;
                                            return (
                                                <tr key={row.id} className={`${!isValid ? 'bg-red-50/50 dark:bg-red-950/20' : 'hover:bg-zinc-100 dark:hover:bg-zinc-800'}`}>
                                                    <td className="px-3 py-2 border-b border-zinc-200 dark:border-zinc-800 text-zinc-400 font-mono">{row.id + 1}</td>
                                                    <td className="px-3 py-2 border-b border-zinc-200 dark:border-zinc-800">
                                                        {isValid ? (
                                                            <span className="text-emerald-600 bg-emerald-100 dark:bg-emerald-900/30 px-2 py-0.5 rounded text-[10px] font-semibold tracking-wide">VALID</span>
                                                        ) : (
                                                            <div className="group relative z-20 tooltip-trigger">
                                                                <span className={`px-2 py-0.5 rounded text-[10px] font-semibold tracking-wide cursor-default ${row.isDuplicate ? 'text-amber-600 bg-amber-100 dark:bg-amber-900/30' : 'text-red-600 bg-red-100 dark:bg-red-900/30'}`}>
                                                                    {row.isDuplicate ? 'DUPLICATE' : 'INVALID'}
                                                                </span>
                                                                <div className="hidden group-hover:block absolute left-full ml-2 top-0 bg-black text-white text-[10px] p-2 rounded shadow-xl whitespace-pre w-48 break-words z-50">
                                                                    {row.errors.join('\n')}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </td>
                                                    {template.columns.map(c => (
                                                        <td key={c} className="px-3 py-2 border-b border-zinc-200 dark:border-zinc-800 whitespace-nowrap truncate max-w-[200px]">
                                                            {row.original[c] || <span className="text-zinc-300">-</span>}
                                                        </td>
                                                    ))}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                             </div>
                        </div>
                    </div>
                )}

                {!importSuccess && (
                     <DialogFooter className="mt-2">
                        <Button variant="outline" onClick={() => !isImporting && onOpenChange(false)} disabled={isAnalyzing || isImporting}>
                            Cancel
                        </Button>
                        {!analyzedData ? (
                            <Button onClick={handleAnalyze} disabled={isAnalyzing} className="bg-blue-600 hover:bg-blue-700 text-white min-w-[100px]">
                                {isAnalyzing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
                                Preview
                            </Button>
                        ) : (
                            <Button onClick={handleImport} disabled={isImporting || validCount === 0} className="bg-emerald-600 hover:bg-emerald-700 text-white min-w-[120px]">
                                {isImporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                                Import Valid ({validCount})
                            </Button>
                        )}
                    </DialogFooter>
                )}
            </DialogContent>
        </Dialog>
    );
}
